import { AnimatePresence, motion } from 'framer-motion';
import React, { FC, useMemo, useState } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from 'recharts';

import { ChartIcon } from '../../../../assets/icons/index';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import Button from '../../../../components/button/index';
import { SelectedCompanyCurrency } from '../../../../components/selected-company-currency/index';
import { Company, useLocale } from '../../../../hooks/index';
import { toMathRound } from '../../../../utils/to-math-round.util';
import { activeColors, fillColors, strokeColors } from '../../../cap-table/graphs/index';
import { GraphVariant } from '../../variables';
import {
  EventGraphProps,
  GraphData,
  notExistingName,
  othersPoint,
  poolUnissuedPoint,
  useEventGraphData,
} from './use-event-graph-data';

export enum EventChartType {
  AREA = 'area',
  BAR = 'bar',
}

interface Stakeholder {
  id: string;
  name: string;
  percentage: number;
  shares: number;
  value: number;
}

const X_AXIS_TICK_MARGIN = 8;
const TICK_LINE_HEIGHT = 14;

interface XAxisTickProps {
  isActive: boolean;
  payload: { value: string };
  x: number;
  y: number;
}

const ValuationAxisTick: FC<XAxisTickProps> = ({ x, y, payload, isActive }) => {
  const [date, name] = payload.value.split('|');
  const fill = isActive ? '#1151B4' : '#475467';

  return (
    <text fill={fill} fontSize={10} fontWeight={500} textAnchor="middle" x={x} y={y}>
      <tspan dy={0} x={x}>
        {name}
      </tspan>
      <tspan dy={TICK_LINE_HEIGHT} x={x}>
        {date}
      </tspan>
    </text>
  );
};

const FullyDilutedAxisTick: FC<XAxisTickProps> = ({ x, y, payload, isActive }) => {
  const fill = isActive ? '#1151B4' : '#475467';

  return (
    <text fill={fill} fontSize={10} fontWeight={500} textAnchor="middle" x={x} y={y}>
      {payload.value}
    </text>
  );
};

const chartMargin = {
  top: 8,
  right: 20,
  left: 8,
  bottom: 20,
};

const CHART_SWITCH_ANIMATION_MS = 320;

interface CustomToolTipProps extends TooltipProps<number | string, keyof GraphData> {
  sharesDivider: number;
  stakeholderColors: Record<
    string,
    {
      fill: string;
      index: number;
    }
  >;
  valuationUsesShareCount: boolean;
  valueDivider: number;
  variant: GraphVariant;
}

const roundMoneyForTooltipDisplay = (amount: number): number => Math.round(Number(amount));

export const CustomToolTip: FC<CustomToolTipProps> = ({
  active,
  payload,
  variant,
  sharesDivider,
  valueDivider,
  valuationUsesShareCount,
  stakeholderColors,
}) => {
  const valuationShowsShareValue = variant === GraphVariant.VALUATION && !valuationUsesShareCount;
  const { selectedCompany: { currency } = {} } = Company.useSelected();
  if (active && payload && payload.length) {
    const point = payload[0].payload;
    const {
      date,
      postMoneyValuation,
      name,
      stakeholders,
      preMoneyValuation,
      isCompanyIncorporation,
      parValue,
      totalIssuedShares,
      totalConsideredPay,
    } = point;

    if (isCompanyIncorporation) {
      const showConsideredPay = variant === GraphVariant.VALUATION;
      const thirdColumnLabel = showConsideredPay ? 'CONSIDERED_PAY' : 'SHARES_PERCENT';

      return (
        <div className="flex w-[364px] flex-col gap-2 rounded-lg bg-gray-25 p-1 pt-2 shadow-2xl">
          <div className="flex justify-between gap-1 border-b-[1px] border-gray-200 px-4 pb-2">
            <span className="text-label-sm font-semibold uppercase text-gray-700">{name}</span>
            <span className="text-label-sm font-semibold text-gray-700">{date}</span>
          </div>
          <div className="flex justify-between gap-1 px-4">
            <span className="text-label-sm font-semibold uppercase text-gray-700">
              <AppFormattedMessage id="TOTAL_ISSUED_SHARES" />
            </span>
            <span className="text-label-sm font-semibold text-forest-600">
              {(totalIssuedShares ?? 0).toLocaleString('en-US')}
            </span>
          </div>
          <div className="flex justify-between gap-1 px-4">
            <span className="text-label-sm font-semibold uppercase text-gray-700">
              <AppFormattedMessage id="PAR_VALUE" />
            </span>
            <span className="flex text-label-sm font-semibold text-forest-600">
              <SelectedCompanyCurrency />
              {parValue}
            </span>
          </div>
          <div className="flex justify-between gap-1 px-4">
            <span className="text-label-sm font-semibold uppercase text-gray-700">
              <AppFormattedMessage id="TOTAL_CONSIDERED_PAY" />
            </span>
            <span className="flex text-label-sm font-semibold text-forest-600">
              <SelectedCompanyCurrency />
              {(totalConsideredPay ?? 0).toLocaleString('en-US', { maximumFractionDigits: 7 })}
            </span>
          </div>
          <div className="grid grid-cols-3">
            <div className="border-b-[1px] border-[#F2F2F2] bg-gray-50 px-4 py-2 text-label-sm font-[500] text-[#172335]">
              <AppFormattedMessage id="STAKEHOLDER" />
            </div>
            <div className="border-b-[1px] border-[#F2F2F2] bg-gray-50 px-4 py-2 text-center text-label-sm font-[500] text-[#172335]">
              <AppFormattedMessage id="SHARES" />
            </div>
            <div className="border-b-[1px] border-[#F2F2F2] bg-gray-50 px-4 py-2 text-center text-label-sm font-[500] text-[#172335]">
              <AppFormattedMessage id={thirdColumnLabel} />
            </div>

            {stakeholders
              .slice()
              .sort((a: Stakeholder, b: Stakeholder) => b.shares - a.shares)
              .map(
                ({
                  name,
                  percentage,
                  shares,
                  id,
                  consideredPay,
                }: { consideredPay?: number } & Stakeholder) => {
                  if (name === notExistingName || shares === 0 || !stakeholderColors[id])
                    return null;

                  return (
                    <React.Fragment key={`${name}_${id}`}>
                      <div className="flex items-center gap-2 truncate border-b border-gray-50 px-4 py-1 text-label-sm font-[500] text-[#172335]">
                        <div
                          className="h-[6px] w-3 shrink-0 rounded-[3px]"
                          style={{ backgroundColor: stakeholderColors[id]?.fill || '#EAECF0' }}
                        />
                        {name}
                      </div>
                      <div className="border-b border-gray-50 px-4 py-1 text-center text-label-sm font-[500] text-[#172335]">
                        {(shares * sharesDivider).toLocaleString('en-US')}
                      </div>
                      <div className="border-b border-gray-50 px-4 py-1 text-center text-label-sm font-[500] text-[#172335]">
                        {showConsideredPay
                          ? `${currency?.symbol}${(consideredPay ?? 0).toLocaleString('en-US', { maximumFractionDigits: 7 })}`
                          : `${percentage.toFixed(0)}%`}
                      </div>
                    </React.Fragment>
                  );
                },
              )}
          </div>
        </div>
      );
    }

    const preMoneyDisplay = roundMoneyForTooltipDisplay(preMoneyValuation);
    const postMoneyDisplay = roundMoneyForTooltipDisplay(postMoneyValuation);

    return (
      <div className="flex w-[364px] flex-col gap-2 rounded-lg bg-gray-25 p-1 pt-2 shadow-2xl">
        <div className="flex justify-between gap-1 border-b-[1px] border-gray-200 px-4 pb-2">
          <span className="text-label-sm font-semibold uppercase text-gray-700">{name}</span>
          <span className="text-label-sm font-semibold text-gray-700">{date}</span>
        </div>
        <div className="flex justify-between gap-1 px-4">
          <span className="text-label-sm font-semibold uppercase text-gray-700">
            <AppFormattedMessage id="PRE_MONEY_VALUATION" />
          </span>
          <span className="text-label-sm font-semibold text-forest-600">
            <SelectedCompanyCurrency /> {preMoneyDisplay.toLocaleString('en-US')}
          </span>
        </div>
        <div className="flex justify-between gap-1 px-4">
          <span className="text-label-sm font-semibold uppercase text-gray-700">
            <AppFormattedMessage id="POST_MONEY_VALUATION" />
          </span>
          <span className="text-label-sm font-semibold text-forest-600">
            <SelectedCompanyCurrency /> {postMoneyDisplay.toLocaleString('en-US')}
          </span>
        </div>
        <div className="grid grid-cols-3">
          <div className="border-b-[1px] border-[#F2F2F2] bg-gray-50 px-4 py-2 text-label-sm font-[500] text-[#172335]">
            <AppFormattedMessage id="STAKEHOLDER" />
          </div>
          <div className="border-b-[1px] border-[#F2F2F2] bg-gray-50 px-4 py-2 text-center text-label-sm font-[500] text-[#172335]">
            <AppFormattedMessage id="SHARES" />
          </div>
          <div className="border-b-[1px] border-[#F2F2F2] bg-gray-50 px-4 py-2 text-center text-label-sm font-[500] text-[#172335]">
            {valuationShowsShareValue ? (
              <AppFormattedMessage id="VALUE" />
            ) : (
              <AppFormattedMessage id="OWNERSHIP_PERCENT" />
            )}
          </div>

          {stakeholders
            .slice()
            .sort((a: Stakeholder, b: Stakeholder) => {
              const getPriority = (id: string): number => {
                if (id === poolUnissuedPoint.id) return 2;
                if (id === othersPoint.id) return 1;
                return 0;
              };

              const aPriority = getPriority(a.id);
              const bPriority = getPriority(b.id);

              if (aPriority !== bPriority) {
                return aPriority - bPriority;
              }

              return b.shares - a.shares;
            })
            .map(({ name, percentage, shares, value, id }: Stakeholder) => {
              if (name === notExistingName || shares === 0 || !stakeholderColors[id]) return null;

              return (
                <React.Fragment key={`${name}_${id}`}>
                  <div className="flex items-center gap-2 truncate border-b border-gray-50 px-4 py-1 text-label-sm font-[500] text-[#172335]">
                    <div
                      className="h-[6px] w-3 shrink-0 rounded-[3px]"
                      style={{ backgroundColor: stakeholderColors[id]?.fill || '#EAECF0' }}
                    />
                    {name}
                  </div>
                  <div className="border-b border-gray-50 px-4 py-1 text-center text-label-sm font-[500] text-[#172335]">
                    {(shares * sharesDivider).toLocaleString('en-US')}
                  </div>
                  <div className="border-b border-gray-50 px-4 py-1 text-center text-label-sm font-[500] text-[#172335]">
                    {valuationShowsShareValue
                      ? `${currency?.symbol}${roundMoneyForTooltipDisplay(value * valueDivider).toLocaleString('en-US')}`
                      : `${percentage.toFixed(0)}%`}
                  </div>
                </React.Fragment>
              );
            })}
        </div>
      </div>
    );
  }
};

export const EventGraph: FC<EventGraphProps> = ({
  variant,
  snapshots,
  currentSnapshot,
  companyIncorporationEvent,
}) => {
  const [activeIndex, setActiveIndex] = React.useState<null | number>(null);
  const [chartType, setChartType] = useState<EventChartType>(EventChartType.AREA);
  const { messagesLocale } = useLocale();
  const {
    allStakeholders,
    normalizedData,
    sharesDivider,
    sharesRound,
    sharesSuffix,
    valuationUsesShareCount,
    valueDivider,
    valueRound,
    valueSuffix,
    yDomain,
  } = useEventGraphData({ snapshots, currentSnapshot, variant, companyIncorporationEvent });

  const chartStacksByShares =
    variant === GraphVariant.OWNERSHIP ||
    (variant === GraphVariant.VALUATION && valuationUsesShareCount);

  const getStakeholderDataKey = (stakeholderIndex: number): string => {
    if (variant === GraphVariant.OWNERSHIP_PERCENT) {
      return `stakeholders[${stakeholderIndex}].percentage`;
    }
    if (chartStacksByShares) {
      return `stakeholders[${stakeholderIndex}].shares`;
    }
    return `stakeholders[${stakeholderIndex}].value`;
  };

  const formatYAxisTick = (value: number): string => {
    if (variant === GraphVariant.OWNERSHIP_PERCENT) {
      return `${value.toFixed(0)}%`;
    }
    if (chartStacksByShares) {
      return `${toMathRound(value, sharesRound)}${sharesSuffix}`;
    }
    return `${toMathRound(value, valueRound)}${valueSuffix}`;
  };

  const handleMouseMove = (e: {
    activePayload?: Array<{ payload: { dateIndex: number } }>;
    activeTooltipIndex?: number;
  }) => {
    if (e.activePayload && e.activePayload.length) {
      setActiveIndex(e.activePayload[0].payload.dateIndex);
      return;
    }

    if (typeof e.activeTooltipIndex === 'number') {
      const point = normalizedData[e.activeTooltipIndex];
      setActiveIndex(point?.dateIndex ?? e.activeTooltipIndex);
    }
  };

  const resolveTooltipPayload = (
    payload: CustomToolTipProps['payload'],
  ): CustomToolTipProps['payload'] => {
    if (payload && payload.length) return payload;

    const point =
      activeIndex != null
        ? normalizedData.find((item) => item.dateIndex === activeIndex)
        : undefined;

    return point
      ? [{ payload: point } as NonNullable<CustomToolTipProps['payload']>[number]]
      : payload;
  };

  const handleMouseLeave = () => {
    setActiveIndex(null);
  };

  const stakeholderColors = useMemo(() => {
    const colorMap: Record<
      string,
      { active: string; fill: string; index: number; stroke: string }
    > = {};

    allStakeholders.forEach((id, index) => {
      colorMap[id] = {
        fill: fillColors[index % fillColors.length],
        index,
        stroke: strokeColors[index % strokeColors.length],
        active: activeColors[index % activeColors.length],
      };
    });

    return colorMap;
  }, [allStakeholders]);

  return (
    <div className="flex h-full w-full flex-col items-end gap-8">
      <Button
        className="flex gap-2 border border-gray-100 bg-white px-2 py-[6px] text-xs font-[450] text-gray-700 shadow-xs"
        onClick={() => {
          setActiveIndex(null);
          setChartType((prev) =>
            prev === EventChartType.AREA ? EventChartType.BAR : EventChartType.AREA,
          );
        }}
        styleType="DEFAULT_ROUNDED"
      >
        <ChartIcon />
        {
          messagesLocale[
            chartType === EventChartType.AREA ? 'SWITCH_TO_BAR_CHART' : 'SWITCH_TO_AREA_CHART'
          ]
        }
      </Button>
      <div className="relative min-h-0 w-full flex-1">
        <AnimatePresence initial={false}>
          <motion.div
            animate={{ opacity: 1 }}
            className="absolute inset-0"
            exit={{ opacity: 0 }}
            initial={{ opacity: 0 }}
            key={`${variant}-${chartType}`}
            transition={{ duration: CHART_SWITCH_ANIMATION_MS / 1000, ease: 'easeInOut' }}
          >
            <ResponsiveContainer height="100%" width="100%">
              {chartType === EventChartType.BAR ? (
                <BarChart
                  data={normalizedData}
                  margin={{ ...chartMargin, top: 0 }}
                  onMouseLeave={handleMouseLeave}
                  onMouseMove={handleMouseMove}
                >
                  <XAxis
                    dataKey="chartLabel"
                    interval="preserveStartEnd"
                    stroke="#475467"
                    tick={({ x, y, payload }) =>
                      payload.value.includes('|') ? (
                        <ValuationAxisTick
                          isActive={activeIndex === payload.index}
                          payload={payload}
                          x={x}
                          y={y}
                        />
                      ) : (
                        <FullyDilutedAxisTick
                          isActive={activeIndex === payload.index}
                          payload={payload}
                          x={x}
                          y={y}
                        />
                      )
                    }
                    tickFormatter={(value, index) => (index % 1 === 0 ? value : '')}
                    tickLine={false}
                    tickMargin={X_AXIS_TICK_MARGIN}
                  />
                  <YAxis
                    domain={yDomain}
                    stroke="#475467"
                    tick={{ fontSize: 10, fontWeight: 500 }}
                    tickFormatter={formatYAxisTick}
                    tickLine={false}
                    tickMargin={16}
                  />

                  {allStakeholders.map((id) => (
                    <Bar
                      animationDuration={CHART_SWITCH_ANIMATION_MS}
                      animationEasing="ease-in-out"
                      className="cursor-pointer"
                      dataKey={getStakeholderDataKey(stakeholderColors[id].index)}
                      fill={stakeholderColors[id].fill}
                      isAnimationActive
                      key={id}
                      stackId="1"
                    />
                  ))}

                  <Tooltip
                    active={activeIndex != null}
                    content={({ active, payload }) => (
                      <CustomToolTip
                        active={Boolean(active || activeIndex != null)}
                        payload={resolveTooltipPayload(payload as CustomToolTipProps['payload'])}
                        sharesDivider={sharesDivider}
                        stakeholderColors={stakeholderColors}
                        valuationUsesShareCount={valuationUsesShareCount}
                        valueDivider={valueDivider}
                        variant={variant}
                      />
                    )}
                    cursor={false}
                  />
                </BarChart>
              ) : (
                <AreaChart
                  data={normalizedData}
                  margin={chartMargin}
                  onMouseLeave={handleMouseLeave}
                  onMouseMove={handleMouseMove}
                >
                  <XAxis
                    dataKey="chartLabel"
                    interval="preserveStartEnd"
                    stroke="#475467"
                    tick={({ x, y, payload }) =>
                      payload.value.includes('|') ? (
                        <ValuationAxisTick
                          isActive={activeIndex === payload.index}
                          payload={payload}
                          x={x}
                          y={y}
                        />
                      ) : (
                        <FullyDilutedAxisTick
                          isActive={activeIndex === payload.index}
                          payload={payload}
                          x={x}
                          y={y}
                        />
                      )
                    }
                    tickFormatter={(value, index) => (index % 1 === 0 ? value : '')}
                    tickLine={false}
                    tickMargin={X_AXIS_TICK_MARGIN}
                  />
                  <YAxis
                    domain={yDomain}
                    stroke="#475467"
                    tick={{ fontSize: 10, fontWeight: 500 }}
                    tickFormatter={formatYAxisTick}
                    tickLine={false}
                    tickMargin={16}
                  />
                  {allStakeholders.map((id) => (
                    <Area
                      activeDot={{
                        strokeWidth: 1,
                        fill: stakeholderColors[id].active,
                      }}
                      animationDuration={CHART_SWITCH_ANIMATION_MS}
                      animationEasing="ease-in-out"
                      className="cursor-pointer"
                      dataKey={getStakeholderDataKey(stakeholderColors[id].index)}
                      fill={stakeholderColors[id].fill}
                      fillOpacity={1}
                      isAnimationActive
                      key={id}
                      stackId="1"
                      stroke={stakeholderColors[id].stroke}
                      type="monotone"
                    />
                  ))}
                  <Tooltip
                    active={activeIndex != null}
                    content={({ active, payload }) => (
                      <CustomToolTip
                        active={Boolean(active || activeIndex != null)}
                        payload={resolveTooltipPayload(payload as CustomToolTipProps['payload'])}
                        sharesDivider={sharesDivider}
                        stakeholderColors={stakeholderColors}
                        valuationUsesShareCount={valuationUsesShareCount}
                        valueDivider={valueDivider}
                        variant={variant}
                      />
                    )}
                    cursor={{ stroke: '#D0D5DD', strokeDasharray: '5 3', strokeWidth: 1 }}
                  />
                </AreaChart>
              )}
            </ResponsiveContainer>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};
